<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import DigitalHumanTechRing from '../DigitalHumanTechRing.vue'
import { playAllMutedSync, requestMutedAutoplay } from '../../utils/mutedAutoplay'
import VideoPlayHint from './VideoPlayHint.vue'
import { LX_BIDS } from './constants'

const props = defineProps<{
  isMobile: boolean
}>()

const MOBILE_TECH_VIDEO =
  'https://s3plus.sankuai.com/human-platform-video/%E6%95%B0%E5%AD%97%E4%BA%BA%E5%B1%95%E7%A4%BA%E6%A8%A1%E5%9D%971.mp4'
/** 移动端视频封面（未起播 / 微信拦截自动播时展示）。 */
const MOBILE_TECH_POSTER =
  'https://p0.meituan.net/mlive/932061ffd3c9a2d969ccff346f0daa8c242974.png'
/** 微信拦截自动播放时仍请求并展示首帧，避免技术区出现整块空白。 */
const MOBILE_TECH_VIDEO_WITH_PREVIEW = `${MOBILE_TECH_VIDEO}#t=0.001`

const mobileVideoRef = ref<HTMLVideoElement | null>(null)
const isMobileVideoPlaying = ref(false)
let autoplayCleanup: (() => void) | undefined
let globalTapBound = false
let hasUserInteracted = false
let visibilityObserver: IntersectionObserver | undefined

const playMobileVideo = () => {
  if (!props.isMobile || !mobileVideoRef.value) return
  playAllMutedSync([mobileVideoRef.value])
}

const onGlobalTap = () => {
  if (!props.isMobile) return
  hasUserInteracted = true
  if (mobileVideoRef.value?.paused) playMobileVideo()
}

const bindGlobalTap = () => {
  if (!props.isMobile || globalTapBound) return
  globalTapBound = true
  document.addEventListener('touchstart', onGlobalTap, { capture: true, passive: true })
  document.addEventListener('click', onGlobalTap, { capture: true, passive: true })
}

const unbindGlobalTap = () => {
  if (!globalTapBound) return
  globalTapBound = false
  document.removeEventListener('touchstart', onGlobalTap, true)
  document.removeEventListener('click', onGlobalTap, true)
}

const onMobileVideoPlaying = () => {
  isMobileVideoPlaying.value = true
}

const onMobileVideoPaused = () => {
  isMobileVideoPlaying.value = false
}

onMounted(() => {
  if (!props.isMobile || !mobileVideoRef.value) return
  bindGlobalTap()
  autoplayCleanup = requestMutedAutoplay(mobileVideoRef.value)

  if ('IntersectionObserver' in window) {
    visibilityObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting)
        if (visible && hasUserInteracted && mobileVideoRef.value?.paused) {
          playMobileVideo()
        }
      },
      { threshold: 0.05 }
    )
    visibilityObserver.observe(mobileVideoRef.value)
  }
})

onBeforeUnmount(() => {
  unbindGlobalTap()
  visibilityObserver?.disconnect()
  autoplayCleanup?.()
})
</script>

<template>
  <section
    id="techSection"
    class="tech-section"
    :data-mv-bid="LX_BIDS.techMv"
    data-mv-key="tech"
    :data-mv-video-name="1"
  >
    <div class="section-heading">
      <div class="section-title">
        <span style="color: #8932FF;">美团官方自研</span>
        <span class="accent">本地生活数字人技术</span>
      </div>
      <div class="section-sub">以真实形象、精准动作和自然表达提升直播体验，助力本地生活商家实现高效经营转化。</div>
    </div>
    <div class="tech-visual">
      <ClientOnly>
        <DigitalHumanTechRing v-if="!isMobile" />
        <div v-else class="tech-mobile-media">
          <video
            ref="mobileVideoRef"
            class="tech-mobile-video"
            :src="MOBILE_TECH_VIDEO_WITH_PREVIEW"
            :poster="MOBILE_TECH_POSTER"
            autoplay
            muted
            loop
            playsinline
            webkit-playsinline
            x5-playsinline
            x5-video-player-type="h5"
            preload="auto"
            disablepictureinpicture
            disableremoteplayback
            aria-label="本地生活数字人技术展示视频"
            @playing="onMobileVideoPlaying"
            @pause="onMobileVideoPaused"
            @ended="onMobileVideoPaused"
          />
          <button
            class="tech-mobile-play"
            type="button"
            aria-label="播放本地生活数字人技术展示视频"
            @click="playMobileVideo"
          >
            <VideoPlayHint v-if="!isMobileVideoPlaying" />
          </button>
        </div>
      </ClientOnly>
    </div>
  </section>
</template>

<style scoped lang="scss">
.tech-section {
  width: 1200px;
  max-width: calc(100% - 48px);
  margin: 160px auto 0;
}

.section-heading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
}

.section-title {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 21px;
  color: #171717;
  font-size: 48px;
  font-weight: 700;
  line-height: normal;
}

.accent {
  color: #171717;
}

.section-sub {
  max-width: 702px;
  color: #666;
  font-size: 18px;
  font-weight: 300; /* L */
  line-height: normal;
}

.tech-visual {
  display: flex;
  justify-content: center;
  min-height: 655px;
}

.tech-mobile-media {
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 671px;
  aspect-ratio: 1;
  border-radius: 32px;
  background: transparent;
}

.tech-mobile-video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.tech-mobile-video::-webkit-media-controls {
  display: none !important;
}

.tech-mobile-play {
  position: absolute;
  z-index: 1;
  inset: 0;
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

@media (max-width: 960px) {
  .tech-section {
    width: auto;
    max-width: none;
    margin: 64px 20px 0;
  }

  .section-title {
    font-size: 28px;
    gap: 8px;
  }

  .section-sub {
    font-size: 16px;
  }

  .tech-visual {
    min-height: 0;
  }
}
</style>
